import React, { useEffect, useMemo, useState } from 'react';
import { Accordion, Box, Button, Flex, IconButton, Spinner, Text } from '@chakra-ui/react';
import { FiAlertTriangle, FiArrowLeft, FiCheck, FiCheckCircle, FiChevronRight, FiClock, FiStar, FiUpload } from 'react-icons/fi';
import type { MarketplaceDispute } from '@foodfluence/shared';
import { MarketplaceOrderStickyHeader } from '../components/MarketplaceOrderStickyHeader';
import { ReportProblemSheet } from '../components/ReportProblemSheet';
import { useNavigate, useParams } from 'react-router-dom';
import type {
  Client,
  MarketplaceOrderDeliverable,
  MarketplaceOrderStatus,
  MarketplaceSubmissionUnit,
} from '@foodfluence/shared';
import { lcApi } from '../../../services/api/lc';
import { ff } from '../../../theme';
import { Pill } from '../../../components/ui';
import { MarketplaceOrderBriefAccordionItems } from '../components/MarketplaceOrderBriefAccordion';
import {
  formatDueDateLabel,
  formatMarketplaceOrderDateLong,
  listMarketplaceDeliverables,
  marketplaceOrderBrandApprovalStatus,
  marketplaceOrderBundleTitle,
  marketplaceOrderInspirationLinks,
  marketplaceOrderInspirationAssetUrls,
  marketplaceOrderIsCanceled,
  marketplaceOrderStepperState,
  marketplaceOrderUploadContentSubmitted,
} from '../utils/marketplaceOrderUtils';

async function fetchCreatorLatestDispute(orderId: string): Promise<(MarketplaceDispute & { id: string }) | null> {
  try {
    const res = await lcApi.getLatestDisputeForOrder(orderId);
    return res.data ?? null;
  } catch {
    return null;
  }
}

export default function MarketplaceOrderSubmissionPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportSheetOpen, setReportSheetOpen] = useState(false);
  const [dispute, setDispute] = useState<(MarketplaceDispute & { id: string }) | null>(null);
  const [order, setOrder] = useState<{
    id: string;
    brandID: string;
    brandName?: string;
    brandLogoUrl?: string;
    brandInstagramHandle?: string;
    cash: number;
    status: MarketplaceOrderStatus;
    cancelReason?: string;
    orderBrief?: any;
    deliverables?: Record<string, MarketplaceOrderDeliverable>;
    submissions?: Record<string, MarketplaceSubmissionUnit>;
  } | null>(null);

  useEffect(() => {
    if (!orderId) {
      setError('Missing order id.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([lcApi.getMarketplaceOrder(orderId), fetchCreatorLatestDispute(orderId)])
      .then(([orderRes, latestDispute]) => {
        if (cancelled) return;
        setOrder(orderRes.data);
        setDispute(latestDispute);
      })
      .catch((e) => {
        console.error('Error loading marketplace order', e);
        if (!cancelled) setError('Could not load order.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const brandName = order?.brandName?.trim() || 'Brand';
  const avatarLetter = (brandName[0] ?? 'B').toUpperCase();

  const inboxBrandClient = useMemo((): Client | undefined => {
    if (!order) return undefined;
    return {
      id: order.brandID,
      accountName: brandName,
      ...(order.brandLogoUrl ? { instagramProfilePicture: order.brandLogoUrl } : {}),
    } as Client;
  }, [order, brandName]);
  const dueText = formatDueDateLabel(order?.orderBrief?.logistics?.contentDeliveryTargetDate ?? null);
  const deliverables = useMemo(
    () => listMarketplaceDeliverables(order?.deliverables ?? null),
    [order?.deliverables]
  );
  const uploadContentSubmitted = useMemo(
    () => marketplaceOrderUploadContentSubmitted(order?.deliverables, order?.submissions),
    [order?.deliverables, order?.submissions]
  );

  const brandApproval = useMemo(
    () => marketplaceOrderBrandApprovalStatus(order?.deliverables, order?.submissions, order?.status ?? 'pendingCreatorAcceptance'),
    [order?.deliverables, order?.submissions, order?.status]
  );

  const stepper = marketplaceOrderStepperState(order?.status ?? 'pendingCreatorAcceptance', order?.deliverables, order?.submissions);

  /** Count: order accepted + inbox (always complete) + upload when submitted + brand approval when approved. */
  const completedActionCount = 2 + (uploadContentSubmitted ? 1 : 0) + (brandApproval === 'approved' ? 1 : 0);

  const allStepsComplete = brandApproval === 'approved' || order?.status === 'completed';
  const headerStatusApproved = allStepsComplete;

  const handleUpload = () => {
    navigate(`/collabs/marketplace-orders/${orderId}/upload`);
  };

  const isCanceled = order ? marketplaceOrderIsCanceled(order.status) : false;

  const handleTalkToBrand = () => {
    if (!orderId) {
      navigate('/inbox/current');
      return;
    }
    navigate('/inbox/current/messages', {
      state: {
        conversationId: orderId,
        campaign: undefined,
        collab: undefined,
        client: inboxBrandClient,
        marketplaceOrderId: orderId,
      },
    });
  };

  if (loading) {
    return (
      <Flex minH="100svh" align="center" justify="center" direction="column" gap={3} bg="white">
        <Spinner size="lg" color={ff.ink} thickness="3px" />
        <Text fontSize="14px" color={ff.text2}>
          Loading order...
        </Text>
      </Flex>
    );
  }

  if (error || !order) {
    return (
      <Flex minH="100svh" align="center" justify="center" direction="column" gap={3} bg="white" px={6}>
        <Text textStyle="display" fontSize="22px" color={ff.ink}>
          Could not load order
        </Text>
        <Text fontSize="14px" color={ff.text2} textAlign="center">
          {error ?? 'Order not found.'}
        </Text>
        <Box>
          <IconButton aria-label="Back" icon={<FiArrowLeft />} variant="inkOutline" h="48px" minW="48px" onClick={() => navigate(-1)} />
        </Box>
      </Flex>
    );
  }

  return (
    <Box bg="white" minH="100svh">
      {orderId && (
        <ReportProblemSheet
          isOpen={reportSheetOpen}
          onClose={() => setReportSheetOpen(false)}
          orderId={orderId}
          onSubmitted={() => {
            setReportSheetOpen(false);
            if (orderId) void fetchCreatorLatestDispute(orderId).then(setDispute);
          }}
        />
      )}
      <MarketplaceOrderStickyHeader
        showSupport
        onBack={() => navigate(-1)}
        avatarLetter={avatarLetter}
        brandName={brandName}
        instagramHandle={order?.brandInstagramHandle}
        trailing={
          <Box textAlign="right">
            <Pill tone="ink">
              <Box as={FiCheck} boxSize="12px" mr="6px" />
              {headerStatusApproved ? 'Approved' : 'Accepted'}
            </Pill>
            <Text mt={1} fontSize="12px" fontWeight="500" color={ff.ink}>
              {dueText ?? ''}
            </Text>
          </Box>
        }
      >
        <Box mt={3} px={1}>
          <Flex align="center" justify="space-between">
            {(['Accepted', 'Uploaded', 'In review', 'Approved'] as const).map((label, idx) => {
              const isDone = allStepsComplete ? true : idx < stepper.active;
              const isActive = allStepsComplete ? false : idx === stepper.active;
              const lineLeftOrange =
                allStepsComplete || (idx !== 0 && idx <= stepper.active);
              const lineRightOrange = allStepsComplete || idx < stepper.active;
              return (
                <Flex key={label} direction="column" align="center" flex={1}>
                  <Flex align="center" w="full">
                    {idx !== 0 && <Box h="2px" bg={lineLeftOrange ? ff.ink : ff.border} flex={1} />}
                    <Flex
                      w="20px"
                      h="20px"
                      borderRadius="full"
                      align="center"
                      justify="center"
                      bg={isDone ? ff.ink : isActive ? ff.lime : 'white'}
                      border="1px solid"
                      borderColor={isDone || isActive ? ff.ink : ff.border}
                      flexShrink={0}
                    >
                      {isDone ? <Box as={FiCheck} color={ff.lime} boxSize={3.5} /> : null}
                    </Flex>
                    {idx !== 3 && <Box h="2px" bg={lineRightOrange ? ff.ink : ff.border} flex={1} />}
                  </Flex>
                  <Text
                    mt={2}
                    textStyle="eyebrow"
                    fontSize="10px"
                    color={isActive || isDone ? ff.ink : ff.muted}
                  >
                    {label}
                  </Text>
                </Flex>
              );
            })}
          </Flex>
        </Box>
      </MarketplaceOrderStickyHeader>

      <Box pb={isCanceled ? 8 : 'calc(24px + env(safe-area-inset-bottom, 0px))'}>
        {/* Canceled banner */}
        {isCanceled && (
          <Box
            mx={4}
            mt={5}
            mb={4}
            px={4}
            py={4}
            borderRadius="20px"
            borderWidth="1px"
            borderColor={ff.line}
            bg={ff.grey}
          >
            <Flex align="center" gap={3}>
              <Flex
                w="36px"
                h="36px"
                borderRadius="12px"
                align="center"
                justify="center"
                bg="white"
                flexShrink={0}
              >
                <Box as={FiAlertTriangle} boxSize="18px" color={ff.danger} />
              </Flex>
              <Box>
                <Text fontSize="15px" fontWeight="600" color={ff.ink}>
                  Order canceled
                </Text>
                <Text fontSize="13px" color={ff.text2} mt={0.5}>
                  {order?.cancelReason ?? 'This order is no longer active.'}
                </Text>
              </Box>
            </Flex>
          </Box>
        )}

        {/* Dispute status banner — shown when there's an active or recently resolved dispute */}
        {dispute && (dispute.status === 'under_review' || dispute.status === 'resolved_canceled' || dispute.status === 'resolved_no_action' || dispute.status === 'resolved_other') && (
          <Box
            as="button"
            onClick={() => setReportSheetOpen(true)}
            mx={4}
            mt={5}
            mb={1}
            px={4}
            py={3}
            minH="44px"
            borderRadius="20px"
            borderWidth="1px"
            borderColor={ff.line}
            bg={dispute.status === 'under_review' ? ff.cream : ff.limeSurface}
            w="calc(100% - 32px)"
            textAlign="left"
          >
            <Flex align="center" gap={3}>
              <Box
                as={dispute.status === 'under_review' ? FiClock : FiCheckCircle}
                boxSize="16px"
                color={ff.ink}
                flexShrink={0}
              />
              <Box flex={1} minW={0}>
                <Text fontSize="13px" fontWeight="600" color={ff.ink}>
                  {dispute.status === 'under_review' ? 'Dispute under review' : 'Dispute resolved'}
                </Text>
                <Text fontSize="12px" color={ff.body} mt={0.5} noOfLines={1}>
                  {dispute.status === 'under_review'
                    ? 'Our team is investigating — tap to view'
                    : 'Tap to read how this was resolved.'}
                </Text>
              </Box>
              <Box as={FiChevronRight} boxSize="14px" color={ff.text2} flexShrink={0} />
            </Flex>
          </Box>
        )}

        {/* Action items — only shown for active orders */}
        {!isCanceled && (
        <Box borderTop="1px solid" borderColor={ff.line}>
          <Flex px={5} py={4} align="center" justify="space-between">
            <Text textStyle="display" fontSize="20px" color={ff.ink}>
              Action items
            </Text>
            <Text fontSize="15px" fontWeight="600" color={ff.ink}>
              {completedActionCount} of 4
            </Text>
          </Flex>
          <Box px={5} pb={4}>
            <Box borderBottom="1px solid" borderBottomColor={ff.line} py={3}>
              <Flex align="center" gap={3}>
                <Flex w="32px" h="32px" borderRadius="9999px" bg={ff.ink} align="center" justify="center">
                  <Box as={FiCheck} color={ff.lime} />
                </Flex>
                <Box>
                  <Text fontSize="15px" fontWeight="600" color={ff.text2} textDecoration="line-through">
                    Order accepted
                  </Text>
                  <Text fontSize="13px" color={ff.text2}>
                    You&apos;re all set to start filming
                  </Text>
                </Box>
              </Flex>
            </Box>

            <Flex
              py={3}
              borderBottom="1px solid" borderBottomColor={ff.line}
              align="center"
              justify="space-between"
              cursor="pointer"
              onClick={handleTalkToBrand}
              _hover={{ bg: ff.grey }}
              borderRadius="12px"
            >
              <Flex align="center" gap={3}>
                <Flex
                  w="32px"
                  h="32px"
                  borderRadius="9999px"
                  bg={ff.ink}
                  align="center"
                  justify="center"
                >
                  <Box as={FiCheck} color={ff.lime} />
                </Flex>
                <Box>
                  <Text fontSize="15px" fontWeight="600" color={ff.text2} textDecoration="line-through">
                    Talk to brand in inbox
                  </Text>
                  <Text fontSize="13px" color={ff.text2}>
                    Tap to open messages with {brandName}
                  </Text>
                </Box>
              </Flex>
              <Box as={FiChevronRight} color={ff.muted} />
            </Flex>

            <Flex
              py={3}
              borderBottom="1px solid" borderBottomColor={ff.line}
              align="center"
              justify="space-between"
              cursor="pointer"
              onClick={handleUpload}
              _hover={{ bg: uploadContentSubmitted ? undefined : ff.grey }}
              borderRadius="12px"
            >
              <Flex align="center" gap={3}>
                <Flex
                  w="32px"
                  h="32px"
                  borderRadius="9999px"
                  border={uploadContentSubmitted ? 'none' : `1px solid ${ff.border}`}
                  bg={uploadContentSubmitted ? ff.ink : 'white'}
                  align="center"
                  justify="center"
                >
                  {uploadContentSubmitted ? <Box as={FiCheck} color={ff.lime} /> : <Box as={FiUpload} color={ff.ink} />}
                </Flex>
                <Box>
                  <Text
                    fontSize="15px"
                    fontWeight="600"
                    color={uploadContentSubmitted ? ff.text2 : ff.ink}
                    textDecoration={uploadContentSubmitted ? 'line-through' : undefined}
                  >
                    Upload content
                  </Text>
                  <Text fontSize="13px" color={ff.text2}>
                    {uploadContentSubmitted
                      ? 'Content submitted for brand review'
                      : 'Submit your draft for brand review'}
                  </Text>
                </Box>
              </Flex>
              <Box as={FiChevronRight} color={ff.muted} />
            </Flex>

            {/* Approved by brand */}
            <Flex
              py={3}
              align="center"
              justify="space-between"
              cursor={brandApproval === 'revision_requested' ? 'pointer' : 'default'}
              onClick={brandApproval === 'revision_requested' ? handleUpload : undefined}
              _hover={{ bg: brandApproval === 'revision_requested' ? ff.grey : undefined }}
              borderRadius="12px"
            >
              <Flex align="center" gap={3}>
                <Flex
                  w="32px"
                  h="32px"
                  borderRadius="9999px"
                  border={brandApproval === 'approved' || brandApproval === 'revision_requested' ? 'none' : `1px solid ${ff.border}`}
                  bg={
                    brandApproval === 'approved'
                      ? ff.ink
                      : brandApproval === 'revision_requested'
                      ? ff.cream
                      : 'white'
                  }
                  align="center"
                  justify="center"
                >
                  {brandApproval === 'approved' ? (
                    <Box as={FiCheck} color={ff.lime} />
                  ) : brandApproval === 'revision_requested' ? (
                    <Box as={FiAlertTriangle} color={ff.danger} />
                  ) : (
                    <Box as={FiStar} color={ff.muted} />
                  )}
                </Flex>
                <Box>
                  <Text
                    fontSize="15px"
                    fontWeight="600"
                    color={brandApproval === 'approved' ? ff.text2 : ff.ink}
                    textDecoration={brandApproval === 'approved' ? 'line-through' : undefined}
                  >
                    {brandApproval === 'revision_requested' ? 'Brand requested revisions' : 'Approved by brand'}
                  </Text>
                  <Text fontSize="13px" color={ff.text2}>
                    {brandApproval === 'approved'
                      ? 'All content approved'
                      : brandApproval === 'revision_requested'
                      ? 'Tap to view'
                      : brandApproval === 'in_review'
                      ? 'Your content is being reviewed'
                      : 'Waiting on your content submission'}
                  </Text>
                </Box>
              </Flex>
              {brandApproval === 'revision_requested' && <Box as={FiChevronRight} color={ff.muted} />}
            </Flex>
          </Box>
        </Box>
        )}

        <Accordion allowMultiple defaultIndex={[0, 1, 2, 3, 4]}>
          <MarketplaceOrderBriefAccordionItems
            model={{
              deliverables,
              bundleTitle: marketplaceOrderBundleTitle(deliverables),
              cash: order.cash,
              narrative: order.orderBrief?.narrative,
              contentBriefFile: (order.orderBrief as { requirements?: { contentBriefFile?: string } })?.requirements
                ?.contentBriefFile,
              fulfillmentType: order.orderBrief?.logistics?.fulfillmentType,
              idealTimelineDisplay: formatMarketplaceOrderDateLong(
                order.orderBrief?.logistics?.contentDeliveryTargetDate ?? null
              ),
              inspirationLinks: marketplaceOrderInspirationLinks(order.orderBrief, order.deliverables),
              inspirationAssetUrls: marketplaceOrderInspirationAssetUrls(order.orderBrief),
              otherNotes: (order.orderBrief as { requirements?: { otherNotes?: string } })?.requirements?.otherNotes,
            }}
          />
        </Accordion>

        <Box
          mt={isCanceled ? 4 : 16}
          pt={8}
          mx={4}
          mb="calc(8px + env(safe-area-inset-bottom, 0px))"
          borderTop="1px solid"
          borderColor={ff.line}
          textAlign="center"
        >
          <Text fontSize="13px" color={ff.text2} lineHeight="1.5">
            {isCanceled ? 'Did you file a dispute on this order?' : 'Having an issue with this order?'}
          </Text>
          <Button
            mt={1}
            variant="link"
            fontWeight="600"
            fontSize="14px"
            color={ff.ink}
            textDecoration="underline"
            minH="44px"
            _hover={{ color: ff.body }}
            onClick={() => setReportSheetOpen(true)}
          >
            {isCanceled ? 'View your report' : 'Report a problem'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

